/**
 * Unified Agent Service
 * 
 * Combines the best features from Agent Mode (MCP tools, voice) and Echobot (context, proactive events)
 * to provide a single, unified AI assistant interface.
 * 
 * Features:
 * - MCP Tool execution (66+ tools)
 * - Context awareness (page, user role, organization stats)
 * - Proactive event detection
 * - Multi-language support (DE/EN)
 * - Chat history management
 * - Voice and text input support
 */

import { GoogleGenAI } from '@google/genai';
import { getMCPServer } from '../mcp';
import { MCPRequest } from '../mcp/types';
import { EchobotContext, EchobotContextData, GeminiEchobotResponse, EchobotTaskToCreate } from '../types/echobot';
import { getAILanguage, type SupportedLanguage } from '../utils/aiLanguageHelper';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';

// Tool categories for better organization
export const TOOL_CATEGORIES = {
  CRM_CORE: ['create_lead', 'list_leads', 'get_lead', 'update_lead', 'search_leads', 'delete_lead'],
  TASKS: ['create_task', 'list_tasks', 'update_task', 'complete_task'],
  OPPORTUNITIES: ['create_opportunity', 'list_opportunities', 'get_opportunity', 'update_opportunity', 'search_opportunities', 'add_note_to_opportunity'],
  COMPANIES: ['create_company', 'list_companies', 'get_company', 'update_company', 'search_companies'],
  BRIEFINGS: ['get_customer_briefing'],
  GOOGLE_ADS: ['list_campaigns', 'get_campaign', 'create_campaign', 'update_campaign'],
  ENGAGEMENTS: ['create_engagement', 'list_engagements', 'get_engagement', 'update_engagement', 'get_team_utilization', 'get_expiring_contracts'],
};

// Context-aware suggested questions
export const SUGGESTED_QUESTIONS: Record<EchobotContext, { de: string[]; en: string[] }> = {
  dashboard: {
    de: ['Zeige meine Aufgaben', 'Wie viele Leads habe ich?', 'Was steht heute an?'],
    en: ['Show my tasks', 'How many leads do I have?', 'What\'s on for today?']
  },
  tasks: {
    de: ['Erstelle einen Task für morgen', 'Welche Tasks sind überfällig?', 'Zeige High-Priority Tasks'],
    en: ['Create a task for tomorrow', 'Which tasks are overdue?', 'Show high-priority tasks']
  },
  leads: {
    de: ['Erstelle einen neuen Lead', 'Suche Lead von Firma...', 'Zeige neue Leads'],
    en: ['Create a new lead', 'Search lead from company...', 'Show new leads']
  },
  opportunities: {
    de: ['Erstelle eine Opportunity', 'Was ist in der Pipeline?', 'Zeige Deals in Negotiation'],
    en: ['Create an opportunity', 'What\'s in the pipeline?', 'Show deals in negotiation']
  },
  'google-ads': {
    de: ['Zeige meine Kampagnen', 'Wie performt meine Werbung?', 'Erstelle eine Kampagne'],
    en: ['Show my campaigns', 'How is my advertising performing?', 'Create a campaign']
  },
  onboarding: {
    de: ['Was muss ich als nächstes tun?', 'Wie funktioniert das CRM?', 'Welche Module brauche ich?'],
    en: ['What should I do next?', 'How does the CRM work?', 'Which modules do I need?']
  },
  stream: {
    de: ['Was ist heute passiert?', 'Zeige Aktivitäten', 'Letzte Änderungen'],
    en: ['What happened today?', 'Show activities', 'Recent changes']
  },
  settings: {
    de: ['Wie ändere ich mein Profil?', 'Wo sind die Einstellungen?', 'Hilfe zu Berechtigungen'],
    en: ['How do I change my profile?', 'Where are the settings?', 'Help with permissions']
  },
  emails: {
    de: ['Zeige ungelesene Emails', 'Erstelle eine Email', 'Email an Lead senden'],
    en: ['Show unread emails', 'Create an email', 'Send email to lead']
  },
  engagements: {
    de: ['Erstelle einen neuen Auftrag', 'Zeige aktive Aufträge', 'Wie ist die Team-Auslastung?', 'Welche Verträge laufen aus?'],
    en: ['Create a new engagement', 'Show active engagements', 'What is the team utilization?', 'Which contracts are expiring?']
  }
};

// Proactive event messages
export const PROACTIVE_EVENTS = {
  overdueTasks: {
    de: (count: number) => `⚠️ Du hast ${count} überfällige Task${count > 1 ? 's' : ''}! Soll ich sie zeigen?`,
    en: (count: number) => `⚠️ You have ${count} overdue task${count > 1 ? 's' : ''}! Want me to show them?`
  },
  noLeads: {
    de: 'Noch keine Leads? Lass mich einen für dich erstellen!',
    en: 'No leads yet? Let me create one for you!'
  },
  inactiveOpportunities: {
    de: (count: number) => `📞 ${count} Opportunity${count > 1 ? 's brauchen' : ' braucht'} Follow-up!`,
    en: (count: number) => `📞 ${count} opportunit${count > 1 ? 'ies need' : 'y needs'} follow-up!`
  },
  expiringContracts: {
    de: (count: number) => `📋 ${count} Vertrag${count > 1 ? 'e laufen' : ' läuft'} in den nächsten 3 Monaten aus!`,
    en: (count: number) => `📋 ${count} contract${count > 1 ? 's are' : ' is'} expiring in the next 3 months!`
  },
  teamOverallocated: {
    de: (count: number) => `⚠️ ${count} Teammitglied${count > 1 ? 'er sind' : ' ist'} überbucht!`,
    en: (count: number) => `⚠️ ${count} team member${count > 1 ? 's are' : ' is'} overbooked!`
  }
};

interface UnifiedAgentConfig {
  orgId: string;
  userId: string;
  language?: SupportedLanguage;
  context?: EchobotContextData;
}

interface AgentResponse {
  message: string;
  animation?: 'celebrate' | 'question' | 'shake' | 'wave' | 'none';
  toolCalls?: Array<{ name: string; result: any }>;
  actionButtons?: Array<{ label: string; action: string }>;
  taskToCreate?: EchobotTaskToCreate;
  proactiveEvent?: string;
}

interface ConversationMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

class UnifiedAgentService {
  private genAI: GoogleGenAI | null = null;
  private conversationHistory: ConversationMessage[] = [];
  private availableTools: any[] = [];
  private config: UnifiedAgentConfig | null = null;
  private initialized: boolean = false;

  /**
   * Initialize the unified agent service
   */
  async initialize(config: UnifiedAgentConfig): Promise<void> {
    if (!GEMINI_API_KEY) {
      throw new Error('Gemini API Key fehlt. Bitte VITE_GEMINI_API_KEY in .env setzen.');
    }

    this.config = config;
    this.genAI = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
    
    // Load MCP tools
    await this.loadMCPTools();
    
    this.initialized = true;
    console.log('✅ Unified Agent Service initialized');
  }

  /**
   * Load available MCP tools
   */
  private async loadMCPTools(): Promise<void> {
    try {
      const mcpServer = getMCPServer();
      
      const toolsRequest: MCPRequest = {
        jsonrpc: '2.0',
        id: 'unified-tools',
        method: 'tools/list',
        params: {
          _context: {
            orgId: this.config?.orgId || '',
            userId: this.config?.userId || ''
          }
        }
      };

      const response = await mcpServer.handleRequest(toolsRequest);
      this.availableTools = response.result?.tools || [];
      console.log(`✅ Loaded ${this.availableTools.length} MCP tools for Unified Agent`);
    } catch (error) {
      console.error('Error loading MCP tools:', error);
      this.availableTools = [];
    }
  }

  /**
   * Get suggested questions based on current context
   */
  getSuggestedQuestions(context: EchobotContext): string[] {
    const lang = this.config?.language || getAILanguage().code;
    return SUGGESTED_QUESTIONS[context]?.[lang] || SUGGESTED_QUESTIONS.dashboard[lang];
  }

  /**
   * Check for proactive events based on context data
   */
  detectProactiveEvent(contextData: EchobotContextData): string | null {
    const lang = this.config?.language || getAILanguage().code;
    const { organizationStats } = contextData;

    // Check for overdue tasks
    if (organizationStats.overdueTasks && organizationStats.overdueTasks > 0) {
      return PROACTIVE_EVENTS.overdueTasks[lang](organizationStats.overdueTasks);
    }

    // Check for no leads
    if (contextData.page === 'dashboard' && organizationStats.leadCount === 0) {
      return PROACTIVE_EVENTS.noLeads[lang];
    }

    // Check for inactive opportunities
    if (organizationStats.inactiveOpportunities && organizationStats.inactiveOpportunities > 0) {
      return PROACTIVE_EVENTS.inactiveOpportunities[lang](organizationStats.inactiveOpportunities);
    }

    return null;
  }

  /**
   * Process user input (text or voice transcription)
   */
  async processInput(
    userMessage: string,
    contextData?: EchobotContextData
  ): Promise<AgentResponse> {
    if (!this.initialized || !this.genAI) {
      throw new Error('Agent not initialized. Call initialize() first.');
    }

    const lang = this.config?.language || getAILanguage().code;
    const isGerman = lang === 'de';

    // Add to conversation history
    this.conversationHistory.push({
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    });

    try {
      // Build tool info for prompt
      const toolInfo = this.availableTools.slice(0, 20).map(t => 
        `${t.name}: ${t.description?.substring(0, 100) || 'No description'}`
      ).join('\n');

      // Build context info
      const contextInfo = contextData ? `
KONTEXT:
- Seite: ${contextData.page}
- Rolle: ${contextData.userRole}
- Leads: ${contextData.organizationStats.leadCount || 0}
- Opportunities: ${contextData.organizationStats.opportunityCount || 0}
- Tasks: ${contextData.organizationStats.taskCount || 0}
- Überfällige Tasks: ${contextData.organizationStats.overdueTasks || 0}
` : '';

      // Build conversation history for context
      const recentHistory = this.conversationHistory.slice(-6).map(m => 
        `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`
      ).join('\n');

      const systemPrompt = isGerman 
        ? `Du bist ein freundlicher CRM-Assistant mit Zugriff auf echte CRM-Funktionen.

VERFÜGBARE TOOLS (nutze sie bei passenden Anfragen):
${toolInfo}

${contextInfo}

REGELN:
1. Antworte auf Deutsch, kurz und natürlich (max 2-3 Sätze)
2. Bei CRM-Aktionen nutze das Format: [TOOL:name|param1=value1|param2=value2]
3. Bei einfachen Fragen antworte direkt ohne Tools
4. Nenne NIEMALS technische IDs - nutze Titel/Namen
5. Sei proaktiv und hilfreich

BEISPIELE:
- "Erstelle einen Task" → [TOOL:create_task|title=Task-Titel|priority=medium] Ich erstelle den Task...
- "Zeige meine Leads" → [TOOL:list_leads] Hier sind deine Leads...
- "Briefing für Microsoft" → [TOOL:get_customer_briefing|companyName=Microsoft] Ich bereite das Briefing vor...
- "Hallo" → Hallo! Wie kann ich dir heute helfen?

AKTIONEN (für actionButtons im JSON):
- Navigation: "navigate-to-dashboard", "navigate-to-tasks", "navigate-to-leads", "navigate-to-opportunities"
- Modals: "open-add-lead-modal", "open-add-task-modal"

Antworte als JSON wenn du actionButtons oder taskToCreate brauchst:
{"message": "...", "animation": "wave|celebrate|question", "actionButtons": [...], "taskToCreate": {...}}`
        : `You are a friendly CRM assistant with access to real CRM functions.

AVAILABLE TOOLS (use when appropriate):
${toolInfo}

${contextInfo}

RULES:
1. Answer in English, short and natural (max 2-3 sentences)
2. For CRM actions use format: [TOOL:name|param1=value1|param2=value2]
3. For simple questions answer directly without tools
4. NEVER mention technical IDs - use titles/names
5. Be proactive and helpful

EXAMPLES:
- "Create a task" → [TOOL:create_task|title=Task-Title|priority=medium] I'm creating the task...
- "Show my leads" → [TOOL:list_leads] Here are your leads...
- "Briefing for Microsoft" → [TOOL:get_customer_briefing|companyName=Microsoft] Preparing the briefing...
- "Hello" → Hello! How can I help you today?

ACTIONS (for actionButtons in JSON):
- Navigation: "navigate-to-dashboard", "navigate-to-tasks", "navigate-to-leads", "navigate-to-opportunities"
- Modals: "open-add-lead-modal", "open-add-task-modal"

Respond as JSON if you need actionButtons or taskToCreate:
{"message": "...", "animation": "wave|celebrate|question", "actionButtons": [...], "taskToCreate": {...}}`;

      const prompt = `${systemPrompt}

Konversation:
${recentHistory}

User: ${userMessage}
Assistant:`;

      // Call Gemini
      const response = await this.genAI.models.generateContent({
        model: 'gemini-2.0-flash',
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.7,
          maxOutputTokens: 500
        }
      });

      let responseText = '';
      try {
        // @ts-ignore
        responseText = response.text || '';
      } catch {
        responseText = response.candidates?.[0]?.content?.parts?.[0]?.text || '';
      }

      // Parse response
      const result = await this.parseAndExecuteResponse(responseText, userMessage);

      // Add to history
      this.conversationHistory.push({
        role: 'assistant',
        content: result.message,
        timestamp: new Date()
      });

      // Check for proactive events
      if (contextData) {
        const proactiveEvent = this.detectProactiveEvent(contextData);
        if (proactiveEvent) {
          result.proactiveEvent = proactiveEvent;
        }
      }

      return result;

    } catch (error: any) {
      console.error('Error processing input:', error);
      
      const errorMessage = isGerman 
        ? 'Entschuldigung, da ist etwas schief gelaufen. Bitte versuche es erneut.'
        : 'Sorry, something went wrong. Please try again.';
      
      return {
        message: errorMessage,
        animation: 'shake'
      };
    }
  }

  /**
   * Parse AI response and execute any tool calls
   */
  private async parseAndExecuteResponse(
    responseText: string,
    originalMessage: string
  ): Promise<AgentResponse> {
    const toolCalls: Array<{ name: string; result: any }> = [];
    
    // Check for JSON response
    let parsedResponse: any = null;
    try {
      if (responseText.trim().startsWith('{')) {
        parsedResponse = JSON.parse(responseText);
      }
    } catch {
      // Not JSON, continue with text parsing
    }

    // Check for tool calls in text
    const toolMatch = responseText.match(/\[TOOL:([^\]]+)\]/);
    let cleanResponse = responseText.replace(/\[TOOL:[^\]]+\]/g, '').trim();

    if (toolMatch) {
      const toolParts = toolMatch[1].split('|');
      const toolName = toolParts[0];
      const args: any = {};
      
      for (let i = 1; i < toolParts.length; i++) {
        const [key, value] = toolParts[i].split('=');
        if (key && value) args[key] = value;
      }

      console.log(`🔧 Executing MCP tool: ${toolName}`, args);

      // Execute tool via MCP
      const toolResult = await this.executeMCPTool(toolName, args);
      toolCalls.push({ name: toolName, result: toolResult });

      // Generate user-friendly response based on tool result
      cleanResponse = await this.generateToolResponse(toolName, args, toolResult, originalMessage);
    }

    // Build final response
    const result: AgentResponse = {
      message: parsedResponse?.message || cleanResponse || responseText,
      animation: parsedResponse?.animation || 'wave',
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
      actionButtons: parsedResponse?.actionButtons,
      taskToCreate: parsedResponse?.taskToCreate
    };

    return result;
  }

  /**
   * Execute an MCP tool
   */
  private async executeMCPTool(toolName: string, args: any): Promise<any> {
    try {
      const mcpServer = getMCPServer();
      
      const request: MCPRequest = {
        jsonrpc: '2.0',
        id: `tool-${Date.now()}`,
        method: 'tools/call',
        params: {
          name: toolName,
          arguments: args,
          _context: {
            orgId: this.config?.orgId || '',
            userId: this.config?.userId || ''
          }
        }
      };

      const response = await mcpServer.handleRequest(request);
      return response.result || response.error;
    } catch (error) {
      console.error(`Error executing ${toolName}:`, error);
      return { error: String(error) };
    }
  }

  /**
   * Generate a user-friendly response based on tool result
   */
  private async generateToolResponse(
    toolName: string,
    args: any,
    result: any,
    originalMessage: string
  ): Promise<string> {
    if (!this.genAI) return 'Tool ausgeführt.';

    const lang = this.config?.language || getAILanguage().code;
    const isGerman = lang === 'de';

    const prompt = isGerman 
      ? `Du hast das Tool "${toolName}" für den User ausgeführt.

ORIGINAL-ANFRAGE: ${originalMessage}
PARAMETER: ${JSON.stringify(args)}
ERGEBNIS: ${JSON.stringify(result).substring(0, 500)}

WICHTIG: 
- Nenne NIEMALS technische IDs oder Datenbank-Keys
- Benutze Titel/Namen aus der Original-Anfrage
- Beispiel: "Task 'Anruf an Max' erstellt" NICHT "Task 'abc123' erstellt"

Antworte dem User kurz, freundlich und natürlich auf Deutsch. Max 1-2 Sätze.`
      : `You executed the tool "${toolName}" for the user.

ORIGINAL REQUEST: ${originalMessage}
PARAMETERS: ${JSON.stringify(args)}
RESULT: ${JSON.stringify(result).substring(0, 500)}

IMPORTANT: 
- NEVER mention technical IDs or database keys
- Use titles/names from the original request
- Example: "Task 'Call Max' created" NOT "Task 'abc123' created"

Respond to the user briefly, friendly and naturally in English. Max 1-2 sentences.`;

    try {
      const response = await this.genAI.models.generateContent({
        model: 'gemini-2.0-flash',
        contents: [{ parts: [{ text: prompt }] }],
        config: { temperature: 0.7, maxOutputTokens: 150 }
      });

      // @ts-ignore
      return response.text || response.candidates?.[0]?.content?.parts?.[0]?.text || '';
    } catch {
      return isGerman ? 'Aktion erfolgreich ausgeführt!' : 'Action completed successfully!';
    }
  }

  /**
   * Get available tools for a specific category
   */
  getToolsByCategory(category: keyof typeof TOOL_CATEGORIES): any[] {
    const toolNames = TOOL_CATEGORIES[category];
    return this.availableTools.filter(t => toolNames.includes(t.name));
  }

  /**
   * Get all available tool names
   */
  getAvailableToolNames(): string[] {
    return this.availableTools.map(t => t.name);
  }

  /**
   * Clear conversation history
   */
  clearHistory(): void {
    this.conversationHistory = [];
  }

  /**
   * Get conversation history
   */
  getHistory(): ConversationMessage[] {
    return [...this.conversationHistory];
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<UnifiedAgentConfig>): void {
    if (this.config) {
      this.config = { ...this.config, ...config };
    }
  }
}

// Export singleton instance
export const unifiedAgentService = new UnifiedAgentService();

// Export class for testing/multiple instances
export { UnifiedAgentService };
